import {match} from 'ts-pattern';

import {FinancingRateMatrixCell, SaveSaleVehicleFinancingBody} from '@dms/api/vehicle';

import {ensureActiveFinancingCell} from './ensureActiveFinancingCell';

/**
 * Replaces every balloon variant of one term/down-payment coordinate with `cells`.
 *
 * The active tuple follows the replacement: it moves to the requested balloon payment when that
 * variant survives, and is cleared when the edited coordinate was the active one and no variant
 * survives. The active-cell guard then re-establishes the tuple on a remaining cell; an empty
 * matrix keeps its current mode and template link with no active tuple.
 */
export function replaceBalloonFinancingVariants(
  body: SaveSaleVehicleFinancingBody,
  term: number,
  downPayment: number,
  cells: FinancingRateMatrixCell[],
  activeBalloonPayment: number | null
) {
  const isCurrentActiveCoordinate =
    body.activeTerm === term && body.activeDownPayment === downPayment;
  const savedActiveBalloonPayment = cells.some(
    (cell) => cell.balloonPayment === activeBalloonPayment
  )
    ? activeBalloonPayment
    : null;
  const nextActiveCoordinates = match({
    hasSavedActiveBalloonPayment: savedActiveBalloonPayment !== null,
    isCurrentActiveCoordinate,
  })
    .with({hasSavedActiveBalloonPayment: true}, () => ({
      activeTerm: term,
      activeDownPayment: downPayment,
      activeBalloonPayment: savedActiveBalloonPayment,
    }))
    .with({hasSavedActiveBalloonPayment: false, isCurrentActiveCoordinate: true}, () => ({
      activeTerm: null,
      activeDownPayment: null,
      activeBalloonPayment: null,
    }))
    .with({hasSavedActiveBalloonPayment: false, isCurrentActiveCoordinate: false}, () => ({
      activeTerm: body.activeTerm,
      activeDownPayment: body.activeDownPayment,
      activeBalloonPayment: body.activeBalloonPayment,
    }))
    .exhaustive();

  return ensureActiveFinancingCell({
    ...body,
    ...nextActiveCoordinates,
    rateMatrix: {
      ...body.rateMatrix,
      cells: [
        ...body.rateMatrix.cells.filter(
          (cell) => cell.term !== term || cell.downPayment !== downPayment
        ),
        ...cells,
      ],
    },
  });
}
